import { ActionIcon, Divider } from "@mantine/core"
import SearchInput from "@/components/form/SearchInput"
import { startTransition, useRef, useState } from "react"
import BaseAction from "../components/drawers/Base.action"
import SelectableItem from "@/components/navigation/SelectableItem"
import { store } from "@/stores/stores"
import { useSnapshot } from "valtio"
import { cn } from "@/lib/utils"
import { BaseDrawerProps } from "../components/drawers/Base.drawer"
import { NodeDefinition, NodeDefinitionCategoryEnum } from "@/gen"
import { NodeCategory } from "@/nodes"

interface AddActionActionProps extends BaseDrawerProps {
  children: React.ReactNode
  replaceGhostNode: (actionType: string) => void
}

const categories = [
  {
    category: NodeDefinitionCategoryEnum.integration,
    label: "Action in an app",
    description: "Do something in an app or service like Google Sheets, Telegram or Notion",
    icon: <IconHugeiconsFallingStar className="size-8 group-hover:text-primary" />,
  },
  {
    category: NodeDefinitionCategoryEnum.transformation,
    label: "Data transformation",
    description: "Manipulate, filter or convert data",
    icon: <IconHugeiconsEdit01 className="size-8 group-hover:text-purple-500" />,
  },
  {
    category: NodeDefinitionCategoryEnum.flow,
    label: "Flow",
    description: "Branch, merge or loop the flow, etc",
    icon: <IconHugeiconsWorkflowCircle05 className="size-8 group-hover:text-amber-500" />,
  },
  {
    category: NodeDefinitionCategoryEnum.core,
    label: "Core",
    description: "Run code, make HTTP requests, set webhooks, etc",
    icon: <IconHugeiconsBackpack03 className="size-8 group-hover:text-pink-500" />,
  },
]

const bottomCategories = [
  {
    category: NodeDefinitionCategoryEnum.trigger,
    label: "Triggers",
    description: "Triggers start your workflow. Workflows can have multiple triggers.",
    icon: <IconHugeiconsZap className="size-8 group-hover:text-emerald-500" />,
  },
]

function AddActionAction(props: AddActionActionProps) {
  const snap = useSnapshot(store)
  const [search, setSearch] = useState("")
  const [_selectedCategory, setSelectedCategory] = useState<NodeCategory | null>(null)
  // const { style, ...rest } = props

  const _categoryActions = _selectedCategory
    ? store.scenario.nodeDefinitions.list.filter(
        (item) => item.category === _selectedCategory || (_selectedCategory === NodeCategory.TRIGGER && item.triggers?.length),
      )
    : []

  const categoryDef = _selectedCategory ? [...categories, ...bottomCategories].find((x) => x.category === _selectedCategory) : null

  const draggable = !snap.scenario.editing?.selectedNode?.node

  const selectedCategory = _selectedCategory || search
  const categoryActions = search
    ? store.scenario.nodeDefinitions.list.filter(
        (item) =>
          item.category !== NodeDefinitionCategoryEnum.internal &&
          (item.label.toLowerCase() + item.description?.toLowerCase()).includes(search.toLowerCase()),
      )
    : _categoryActions

  return (
    <BaseAction
      title={
        <div className="flex flex-col gap-1 w-full">
          <div className="flex h-[34px] items-center gap-1 w-full">
            {categoryDef && (
              <ActionIcon color="dark" variant={"subtle"} size={"lg"} type="button" onClick={() => setSelectedCategory(null)}>
                <IconMaterialSymbolsArrowBackRounded className="size-6 text-secondary" />
              </ActionIcon>
            )}
            {categoryDef?.label || "Actions"}
          </div>
          <SearchInput
            data-autofocus
            placeholder="Search actions..."
            onChange={(e) => {
              startTransition(() => setSearch(e.target.value))
            }}
            className={"w-full max-w-full font-medium"}
            wrapperClassName="w-full max-w-full"
          />
        </div>
      }
      {...props}
    >
      {selectedCategory ? (
        <ActionsList nodes={categoryActions} draggable={draggable} replaceGhostNode={props.replaceGhostNode} />
      ) : (
        <>
          {categories.map((cat) => (
            <SelectableItem
              {...cat}
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              rightIcon={<IconHugeiconsArrowRight02 />}
            />
          ))}
          {draggable && (
            <>
              <Divider className="my-2" />
              {bottomCategories.map((cat) => (
                <SelectableItem
                  {...cat}
                  key={cat.category}
                  onClick={() => setSelectedCategory(cat.category)}
                  rightIcon={<IconHugeiconsArrowRight02 />}
                />
              ))}
            </>
          )}
        </>
      )}
    </BaseAction>
  )
}

export default AddActionAction

//  █████╗  ██████╗████████╗██╗ ██████╗ ███╗   ██╗███████╗██╗     ██╗███████╗████████╗
// ██╔══██╗██╔════╝╚══██╔══╝██║██╔═══██╗████╗  ██║██╔════╝██║     ██║██╔════╝╚══██╔══╝
// ███████║██║        ██║   ██║██║   ██║██╔██╗ ██║███████╗██║     ██║███████╗   ██║
// ██╔══██║██║        ██║   ██║██║   ██║██║╚██╗██║╚════██║██║     ██║╚════██║   ██║
// ██║  ██║╚██████╗   ██║   ██║╚██████╔╝██║ ╚████║███████║███████╗██║███████║   ██║
// ╚═╝  ╚═╝ ╚═════╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚══════╝╚═╝╚══════╝   ╚═╝
//
const ActionsList = (props: { nodes: NodeDefinition[]; draggable: boolean; replaceGhostNode: (actionType: string) => void }) => {
  const { nodes, ...rest } = props

  return (
    <div className="animate-fade-left animate-duration-200">
      {props.nodes.map((node) => (
        <ActionDraggable {...rest} node={node} key={node.type} />
      ))}
    </div>
  )
}

const ActionDraggable = (props: { node: NodeDefinition; draggable: boolean; replaceGhostNode: (actionType: string) => void }) => {
  const dragPreviewRef = useRef(null)

  const onDragStart = (event, nodeType) => {
    if (!props.draggable) return

    store.scenario.editing.draggingNode = true
    event.dataTransfer.setData("application/reactflow", nodeType)
    event.dataTransfer.effectAllowed = "move"

    // Criando uma visualização customizada do nó
    const dragPreview = dragPreviewRef.current
    if (dragPreview) {
      event.dataTransfer.setDragImage(dragPreview, 0, 0)
    }
  }

  return (
    <div
      key={props.node.type}
      draggable={props.draggable}
      {...(props.draggable
        ? {
            onDragStart: (event) => onDragStart(event, props.node.type + "::" + props.node.actions?.[0].id + "::" + props.node.actions?.[0]?.operations?.[0]?.id),
            onDragEnd: () => {
              store.scenario.editing.draggingNode = false
            },
          }
        : {
            onClick: () => {
              props.replaceGhostNode(props.node.type)
            },
          })}
    >
      <SelectableItem
        label={props.node.label}
        description={props.node.description}
        className={cn(
          props.draggable &&
            ` cursor-grab border-2 border-dashed border-transparent active:cursor-grabbing active:border-fg-tertiary hover:border-border
            hover:bg-transparent `,
        )}
        icon={
          <div className="flex items-center gap-4">
            {props.draggable && <IconTeenyiconsDragVerticalOutline className="text-fg-tertiary" />}
            <div
              className={"rounded-2xl text-white fc size-11"}
              style={{
                backgroundColor: props.node.color,
                ...(props.node.text_color && { color: props.node.text_color }),
                transform: "translate(0, 0)",
              }}
              ref={dragPreviewRef}
            >
              {props.node.image_src && <img src={props.node.image_src} className="size-5" />}
              {/* {(props.action as any).iconElement ? (
                <props.action.iconElement className="size-5" />
              ) : (
                <img src={props.action.image_src} className="size-5" />
              )} */}
            </div>
          </div>
        }
      />
    </div>
  )
}
